import { Button, Input } from '@fsd/ui';
import {
  AddSquare,
  Bag2,
  Box,
  Cart,
  Magnifer,
  Pen,
  TrashBinTrash,
  Widget2,
} from '@solar-icons/react';
import { useState } from 'react';
import type { ProductFormData } from '../../types/product';
import { DeleteConfirmModal } from '../deleteConfirmModal';

interface ProductListProps {
  products: ProductFormData[];
  isLoading: boolean;
  category: 'new-drones' | 'used-drones' | 'buffalo' | 'power-citros' | 'parts';
  onAddNew: () => void;
  onEdit: (product: ProductFormData) => void;
  onDelete: (product: ProductFormData, onSuccess: () => void) => void;
  isDeleting?: boolean;
}

const categoryConfig = {
  'new-drones': {
    title: 'Drones Novos',
    description: 'Gerencie seu catálogo de drones novos',
    emptyTitle: 'Nenhum drone novo cadastrado',
    emptyDescription: 'Comece adicionando seu primeiro drone novo ao catálogo',
    addButtonText: 'Adicionar Drone',
    icon: Bag2,
    iconColor: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
  },
  'used-drones': {
    title: 'Drones Usados',
    description: 'Gerencie seu catálogo de drones usados',
    emptyTitle: 'Nenhum drone usado cadastrado',
    emptyDescription: 'Adicione drones usados disponíveis para venda',
    addButtonText: 'Adicionar Drone Usado',
    icon: Box,
    iconColor: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10',
  },
  buffalo: {
    title: 'Linha Buffalo',
    description: 'Gerencie seu catálogo de motores, motobombas e acoplados Buffalo',
    emptyTitle: 'Nenhum item da Linha Buffalo cadastrado',
    emptyDescription: 'Adicione motores e acoplados da Linha Buffalo ao catálogo',
    addButtonText: 'Adicionar Item Buffalo',
    icon: Cart,
    iconColor: 'text-amber-600',
    bgColor: 'bg-amber-500/10',
  },
  'power-citros': {
    title: 'Power Citros',
    description: 'Gerencie os produtos Power Citros — ultra limpador desengraxante cítrico',
    emptyTitle: 'Nenhum produto Power Citros cadastrado',
    emptyDescription: 'Adicione os produtos da linha Power Citros ao catálogo',
    addButtonText: 'Adicionar Produto Power Citros',
    icon: Bag2,
    iconColor: 'text-lime-600',
    bgColor: 'bg-lime-500/10',
  },
  parts: {
    title: 'Peças e Acessórios',
    description: 'Gerencie suas peças e acessórios',
    emptyTitle: 'Nenhuma peça cadastrada',
    emptyDescription: 'Adicione peças e acessórios ao seu catálogo',
    addButtonText: 'Adicionar Peça',
    icon: Widget2,
    iconColor: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
  },
};

export function ProductList({
  products,
  isLoading,
  category,
  onAddNew,
  onEdit,
  onDelete,
  isDeleting,
}: ProductListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [productToDelete, setProductToDelete] = useState<ProductFormData | null>(null);
  const config = categoryConfig[category];

  // Filtrar produtos pela busca
  const filteredProducts = products.filter(
    (product) =>
      product.name['pt-BR'].toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description['pt-BR'].toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleDeleteClick = (product: ProductFormData) => {
    setProductToDelete(product);
  };

  const handleConfirmDelete = () => {
    if (productToDelete) {
      onDelete(productToDelete, () => {
        setProductToDelete(null);
      });
    }
  };

  const handleCancelDelete = () => {
    setProductToDelete(null);
  };

  // Loading State
  if (isLoading) {
    return (
      <div className="space-y-6">
        <ProductListHeader config={config} onAddNew={onAddNew} />
        <LoadingSkeleton />
      </div>
    );
  }

  // Empty State
  if (products.length === 0) {
    return (
      <div className="space-y-6">
        <ProductListHeader config={config} onAddNew={onAddNew} />
        <EmptyState config={config} onAddNew={onAddNew} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <ProductListHeader config={config} onAddNew={onAddNew} />

      {/* Search Bar */}
      <div className="relative">
        <Magnifer
          size={18}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Buscar produtos..."
          className="pl-10"
        />
      </div>

      {/* Stats Bar */}
      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
        <span>
          <strong className="text-foreground">{products.length}</strong> produto(s) cadastrado(s)
        </span>
        {searchTerm && (
          <span>
            <strong className="text-foreground">{filteredProducts.length}</strong> resultado(s) para
            "{searchTerm}"
          </span>
        )}
      </div>

      {/* Products Grid */}
      {filteredProducts.length > 0 ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredProducts.map((product) => (
            <ProductCard
              key={product.id || product.slug}
              product={product}
              onEdit={() => onEdit(product)}
              onDelete={() => handleDeleteClick(product)}
              isDeleting={isDeleting}
              categoryColor={config.iconColor}
            />
          ))}
        </div>
      ) : (
        <NoResults searchTerm={searchTerm} onClear={() => setSearchTerm('')} />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={!!productToDelete}
        productName={productToDelete?.name['pt-BR'] || ''}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
}

// Sub-componentes

function ProductListHeader({
  config,
  onAddNew,
}: {
  config: (typeof categoryConfig)[keyof typeof categoryConfig];
  onAddNew: () => void;
}) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{config.title}</h1>
        <p className="mt-1 text-sm text-muted-foreground sm:text-base">{config.description}</p>
      </div>
      <Button onClick={onAddNew} className="w-full sm:w-auto">
        <AddSquare size={18} className="mr-2" />
        {config.addButtonText}
      </Button>
    </div>
  );
}

function EmptyState({
  config,
  onAddNew,
}: {
  config: (typeof categoryConfig)[keyof typeof categoryConfig];
  onAddNew: () => void;
}) {
  const Icon = config.icon;

  return (
    <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-border bg-card/50 p-8 text-center sm:p-12">
      <div className={`mb-4 rounded-full ${config.bgColor} p-4`}>
        <Icon size={40} className={config.iconColor} />
      </div>
      <h3 className="mb-2 text-lg font-semibold text-foreground sm:text-xl">{config.emptyTitle}</h3>
      <p className="mb-6 max-w-md text-sm text-muted-foreground sm:text-base">
        {config.emptyDescription}
      </p>
      <Button onClick={onAddNew} size="lg">
        <AddSquare size={20} className="mr-2" />
        {config.addButtonText}
      </Button>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="animate-pulse rounded-lg border border-border bg-card p-4">
          <div className="mb-4 h-40 rounded-md bg-muted" />
          <div className="mb-2 h-5 w-3/4 rounded bg-muted" />
          <div className="mb-4 h-4 w-1/2 rounded bg-muted" />
          <div className="flex gap-2">
            <div className="h-8 flex-1 rounded bg-muted" />
            <div className="h-8 w-8 rounded bg-muted" />
          </div>
        </div>
      ))}
    </div>
  );
}

function NoResults({ searchTerm, onClear }: { searchTerm: string; onClear: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-card/50 p-8 text-center">
      <Magnifer size={40} className="mb-4 text-muted-foreground/50" />
      <h3 className="mb-2 text-lg font-semibold text-foreground">Nenhum resultado encontrado</h3>
      <p className="mb-4 text-sm text-muted-foreground">
        Não encontramos produtos com "{searchTerm}"
      </p>
      <Button variant="outline" onClick={onClear}>
        Limpar busca
      </Button>
    </div>
  );
}

function ProductCard({
  product,
  onEdit,
  onDelete,
  isDeleting,
  categoryColor,
}: {
  product: ProductFormData;
  onEdit: () => void;
  onDelete: () => void;
  isDeleting?: boolean;
  categoryColor: string;
}) {
  const hasPrice = product.price && product.price > 0;

  return (
    <div className="group relative overflow-hidden rounded-lg border border-border bg-card transition-all hover:border-primary/50 hover:shadow-md">
      {/* Image */}
      <div className="relative aspect-4/3 overflow-hidden bg-muted">
        {product.image ? (
          <img
            src={product.image}
            alt={product.name['pt-BR']}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Box size={40} className="text-muted-foreground/30" />
          </div>
        )}

        {/* Featured Badge */}
        {product.featured && (
          <div className="absolute left-2 top-2 rounded-full bg-yellow-500 px-2 py-1 text-xs font-medium text-white shadow-sm">
            Destaque
          </div>
        )}

        {/* Status Badge */}
        <div
          className={`absolute right-2 top-2 rounded-full px-2 py-1 text-xs font-medium shadow-sm ${
            product.status === 'active'
              ? 'bg-green-500/90 text-white'
              : product.status === 'draft'
                ? 'bg-yellow-500/90 text-white'
                : 'bg-gray-500/90 text-white'
          }`}
        >
          {product.status === 'active'
            ? 'Ativo'
            : product.status === 'draft'
              ? 'Rascunho'
              : 'Inativo'}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="mb-1 line-clamp-1 text-sm font-semibold text-foreground sm:text-base">
          {product.name['pt-BR']}
        </h3>

        {product.subtitle?.['pt-BR'] && (
          <p className="mb-2 line-clamp-1 text-xs text-muted-foreground">
            {product.subtitle['pt-BR']}
          </p>
        )}

        {/* Price */}
        <div className="mb-4">
          {hasPrice ? (
            <span className={`text-lg font-bold ${categoryColor}`}>
              {new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
              }).format(product.price as number)}
            </span>
          ) : (
            <span className="text-sm italic text-muted-foreground">Consulte o preço</span>
          )}
        </div>

        {/* Stock indicator */}
        <div className="mb-4 flex items-center gap-2">
          <span
            className={`h-2 w-2 rounded-full ${product.inStock ? 'bg-green-500' : 'bg-red-500'}`}
          />
          <span className="text-xs text-muted-foreground">
            {product.inStock ? 'Em estoque' : 'Indisponível'}
          </span>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="flex-1" onClick={onEdit}>
            <Pen size={14} className="mr-1" />
            Editar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onDelete}
            disabled={isDeleting}
            className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
          >
            <TrashBinTrash size={14} />
          </Button>
        </div>
      </div>
    </div>
  );
}
